import React from 'react';
import { momentoItaliano } from '../../utils/oraItaliana';

/**
 * Il riquadro della riconciliazione del conto prodotto per medico: uscito
 * dai lotti, attribuito, non attribuito, e le frasi che li spiegano. Lo
 * vede solo chi coordina (il server manda `riconciliazione` solo a lui).
 *
 * Sta in un file suo per poterlo disegnare da solo: provaConsumoOrfano.js
 * lo rende con la risposta vera del controller, sui dati di un database usa
 * e getta, e guarda che cosa compare. Qui NON si calcola niente sui
 * numeri: le somme, i lotti fuori posto e il confine nel tempo li manda il
 * server, e lo schermo li mostra.
 */

export const num = (v) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('it-IT', { maximumFractionDigits: 4 });
};

/* Il nome di un lotto nelle frasi. Il codice puo' mancare (un lotto
   caricato senza codice, su un prodotto senza tracciabilita' rigorosa), e
   fino al 7/10/2026 la frase scriveva «null (Prodotto, -1)»: il ripiego e'
   il numero del lotto, che c'e' sempre. */
const nomeLotto = (l) => (l.codice_lotto ? l.codice_lotto : `lotto n. ${l.lotto_id}`);
const elencoLotti = (lotti, quanto, scrivi = num) => lotti
    .map((l) => `${nomeLotto(l)} (${l.prodotto ? `${l.prodotto}, ` : ''}${scrivi(quanto(l))})`)
    .join(' · ');

/* Una quantita' col segno anche sopra zero (revisione del beta su 0be41e9,
   7/10/2026): nell'elenco del non attribuito stanno lotti dei due versi, e
   «1» accanto a «-1» si legge come una quantita', non come «sopra zero». */
const conSegno = (v) => (parseFloat(v) > 0 ? `+${num(v)}` : num(v));

const RiquadroRiconciliazione = ({ riconciliazione }) => {
    if (!riconciliazione) return null;
    const r = riconciliazione;
    /* I lotti col consumo senza procedura sotto zero li sceglie il server,
       lotto per lotto (revisione del beta, 7/10/2026): la somma qui sopra
       puo' essere positiva anche con un lotto a -1 accanto a uno a +14, e
       fino a quel giorno il rosso guardava la somma. */
    const negativi = r.lotti_consumo_negativo || [];
    /* Lo stesso per il non attribuito (revisione del beta su 7a04aa2,
       7/10/2026): il rosso guardava la somma, e un lotto a +1 accanto a uno
       a -1 faceva zero. I lotti diversi da zero e il loro segno li manda il
       server; qui si sceglie solo la frase. */
    const nonAttribuiti = r.lotti_non_attribuito || [];
    const sopra = nonAttribuiti.filter((l) => l.segno === 'positivo');
    const sotto = nonAttribuiti.filter((l) => l.segno === 'negativo');
    const allarme = nonAttribuiti.length > 0 || negativi.length > 0 || !r.torna;
    /* Il confine fra le due frasi lo manda il server: e' il momento in cui
       e' stata applicata la migration 292, da cui ogni storno dice quale
       scarico restituisce (in produzione la sera del 3/10/2026, non la
       mezzanotte che qui era scritta). */
    const confine = r.storni_senza_riferimento_fino_al
        ? momentoItaliano(r.storni_senza_riferimento_fino_al)
        : null;
    return (
        <div className={`mag-conti-testa ${allarme ? 'mag-conti-testa--allarme' : ''}`}>
            <p>
                Uscito dai lotti <b>{num(r.uscito_netto)}</b> ·
                attribuito a un medico <b>{num(r.attribuito)}</b> ·
                non attribuito{' '}
                <b className={nonAttribuiti.length > 0 ? 'mag-critico' : ''}>
                    {num(r.non_attribuito)}
                </b>
            </p>
            {/* Dalla migration 309 il consumo orfano e' al netto dei suoi
                storni: una prestazione tolta, il cui storno dice quale
                scarico restituisce, non compare piu' qui. Restano lo
                scarico senza procedura e le prestazioni tolte prima
                della 292, il cui storno non lo dice (contato fra i
                resi). Con quei resi il consumo qui sotto contiene merce
                che E' rientrata, e non si puo' dire «non rientrato»: non
                rientrato e' il non attribuito, la differenza. Senza, le
                due cifre coincidono e la frase corta e' vera.
                Il non attribuito qui sopra e' la somma dei lotti: quando il
                server ne nomina qualcuno fuori posto (l'elenco in rosso
                qui sotto), la somma puo' fare zero o -1 mentre un lotto a +1
                non e' rientrato, e la frase rimanda all'elenco (revisione
                del beta su 0be41e9, 7/10/2026). Lo stesso senza resi di
                prima (revisione del beta su 75dc6e5, 7/10/2026): il consumo
                qui sotto e' anche lui la somma dei lotti, e con un lotto a +2
                e uno a -1 la frase diceva «1 ... non risulta rientrato»
                mentre il rosso diceva +2. */}
            {parseFloat(r.consumo_orfano) > 0 && (
                <p className="mag-sub">
                    {parseFloat(r.resi) > 0 ? (
                        <>
                            {num(r.consumo_orfano)} di consumo è uscito dal magazzino senza una procedura a cui risulti.
                            Accanto, {num(r.resi)} è rientrato con gli storni delle prestazioni tolte
                            {confine ? ` fino al ${confine.data} alle ${confine.ora}` : ' con il programma di prima'}:
                            quegli storni non dicono quale scarico restituiscono, quindi si contano a parte.
                            {nonAttribuiti.length > 0
                                ? ' Quello che non risulta rientrato si guarda lotto per lotto, nell\'elenco in rosso qui sotto: il non attribuito qui sopra è la somma dei lotti, e lotti di segno diverso si compensano.'
                                : ' Quello che non risulta rientrato è il non attribuito qui sopra, la differenza fra le due cifre.'}
                            {' '}Una prestazione tolta dopo non compare qui: il suo storno dice quale
                            scarico restituisce. Se il non attribuito cresce, va guardato.
                        </>
                    ) : (
                        <>
                            {nonAttribuiti.length > 0
                                ? `${num(r.consumo_orfano)} di consumo è uscito dal magazzino senza una procedura a cui risulti: è la somma dei lotti, e lotti di segno diverso si compensano. Quello che non risulta rientrato si guarda lotto per lotto, nell'elenco in rosso qui sotto.`
                                : `${num(r.consumo_orfano)} di consumo è uscito dal magazzino senza una procedura a cui risulti, e non risulta rientrato.`}
                            {' '}Una prestazione tolta non compare qui: il suo storno dice quale scarico
                            restituisce. Se questo numero cresce, va guardato.
                        </>
                    )}
                </p>
            )}
            {negativi.length > 0 && (
                <p className="mag-critico">
                    Il consumo senza procedura risulta negativo {negativi.length === 1 ? 'in un lotto' : `in ${negativi.length} lotti`}:{' '}
                    {elencoLotti(negativi, (l) => l.consumo_orfano)}.
                    Per quegli scarichi è rientrato più di quanto era uscito. Va segnalato prima di usare
                    questi numeri.
                </p>
            )}
            {nonAttribuiti.length > 0 && (
                <p className="mag-critico">
                    Il non attribuito non è zero {nonAttribuiti.length === 1 ? 'in un lotto' : `in ${nonAttribuiti.length} lotti`}:{' '}
                    {elencoLotti(nonAttribuiti, (l) => l.non_attribuito, conSegno)}.
                    {sopra.length > 0 && ' Sopra zero: è uscito dal magazzino e non risulta né a un medico né rientrato.'}
                    {sotto.length > 0 && ' Sotto zero: è rientrato più di quanto risulta uscito senza un medico.'}
                    {' '}Si guarda lotto per lotto, perché sommati i lotti possono fare zero.
                </p>
            )}
            {!r.torna && (
                <p className="mag-critico">
                    I conti e il magazzino non raccontano la stessa storia: attribuito più
                    non attribuito non fa quanto è uscito. Va segnalato prima di usare
                    questi numeri.
                </p>
            )}
        </div>
    );
};

export default RiquadroRiconciliazione;
